import { useMemo } from "react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { MenuButton } from "./MenuButton";
import type { LocaleKey } from "../utils/svgPlaceholders";

export interface MenuItem {
  key: string;
  path: string;
  label: string;
  accent?: { start: string; end: string };
  graphic?: string;
}

export const getMenuGraphic = (locale: LocaleKey, slug: string) => {
  const suffix = locale === "en" ? "-en" : "";
  const filename =
    slug === "language"
      ? locale === "en"
        ? "language-btn.png"
        : "language-btn-en.png"
      : `${slug}-btn${suffix}.png`;
  return `/basiclaw2025-26/assets/menu/${filename}`;
};

interface ColumnRightProps {
  locale: LocaleKey;
  items?: MenuItem[];
  children?: ReactNode;
  onMenuNavigate?: (key: string) => void;
}

export const ColumnRight = ({
  locale,
  items,
  children,
  onMenuNavigate,
}: ColumnRightProps) => {
  const { t } = useTranslation();
  const prefix = locale === "en" ? "/en" : "";

  const computedItems = useMemo(
    () =>
      items ??
      [
        {
          key: "home",
          path: "",
          label: t("navigation.home"),
          accent: { start: "#ec4899", end: "#f97316" },
          graphic: getMenuGraphic(locale, "home"),
        },
        {
          key: "competition-details",
          path: "competition-details",
          label: t("navigation.competitionDetails"),
          accent: { start: "#6366f1", end: "#8b5cf6" },
          graphic: getMenuGraphic(locale, "competition-details"),
        },
        {
          key: "join-now",
          path: "join-now",
          label: t("navigation.joinNow"),
          accent: { start: "#22c55e", end: "#16a34a" },
          graphic: getMenuGraphic(locale, "join-now"),
        },
        {
          key: "event-details",
          path: "event-details",
          label: t("navigation.eventDetails"),
          accent: { start: "#0ea5e9", end: "#38bdf8" },
          graphic: getMenuGraphic(locale, "quiz-final"),
        },
        {
          key: "try-it-now",
          path: "try-it-now",
          label: t("navigation.tryItNow"),
          accent: { start: "#f59e0b", end: "#f97316" },
          graphic: getMenuGraphic(locale, "try-it-now"),
        },
        {
          key: "related-websites",
          path: "related-websites",
          label: t("navigation.relatedWebsites"),
          accent: { start: "#6366f1", end: "#3b82f6" },
          graphic: getMenuGraphic(locale, "related-websites"),
        },
      ],
    [items, locale, t],
  );

  const toUrl = (path: string) => {
    const suffix = path ? `/${path}/` : "/";
    return `${prefix}${suffix}`;
  };

  const handleMenuClick = (key: string) => () => {
    onMenuNavigate?.(key);
    if (typeof window !== "undefined") {
      // Scroll to top after navigation kicks in
      window.requestAnimationFrame(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      });
    }
  };

  return (
    <div className="column-right">
      <div className="column-right__menu" role="navigation">
        {computedItems.map((item) => (
          <MenuButton
            key={`${item.key}-${locale}`}
            to={toUrl(item.path)}
            label={item.label}
            accent={item.accent}
            graphic={item.graphic ?? getMenuGraphic(locale, item.key)}
            locale={locale}
            onClick={handleMenuClick(item.key)}
          />
        ))}
      </div>
      {children ? <div className="column-right__extra">{children}</div> : null}
    </div>
  );
};
